/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Link, Typography2, typography2Color } from '@coursera/cds-core';
import type { ButtonName, SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedButton from 'bundles/page/components/TrackedButton';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import PartnerSquareLogo from 'bundles/unified-description-page-common/components/shared/PartnerSquareLogo';
import { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import PartnerModal from 'bundles/unified-description-page-common/components/shared/modals/PartnerModal';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { PartnerFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import { getPartnerPageUrl } from 'bundles/unified-description-page-common/utils';

import _t from 'i18n!nls/xdp';

const styles = {
  container: css`
    display: flex;
    gap: var(--cds-spacing-200);
    align-items: start;
  `,
  partnerInfo: css`
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: var(--cds-spacing-100);
  `,
  partnerName: css`
    text-decoration-color: var(--cds-color-neutral-primary);
  `,
  learnMore: css`
    max-width: unset;
    padding: 0;
    margin-left: 0;
    height: unset;
    width: unset;

    :hover {
      background: none;
      box-shadow: none;
      border: none;
    }
  `,
  learnMoreOverrides: css`
    ${typography2Color.supportText};
    text-decoration: underline;

    &:hover {
      ${typography2Color.supportText};
      text-decoration: none;
    }
  `,
};

type Props = {
  partner: PartnerFragment;
  trackingName?: string;
  disableCTA?: boolean;
  trackingLocation?: SectionName;
};

const Partner = ({ partner, trackingName, disableCTA, trackingLocation }: Props) => {
  const { isModalOpen, setModalOpen } = useModal();
  const eventingProductData = useEventingProductData();
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;
  const track = useTracker();

  const styledPartnerName = (
    <Typography2 component="span" variant="subtitleMedium">
      {partner.name}
    </Typography2>
  );

  const onClickToTrack = (linkName: ButtonName) => () => {
    track('click_button', {
      button: { name: linkName },
      ...(trackingLocation
        ? {
            pageSection: {
              sectionName: trackingLocation,
            },
          }
        : {}),
      ...eventingProductData,
    });
  };

  return (
    <div css={styles.container}>
      {partner.squareLogo && <PartnerSquareLogo src={partner.squareLogo} />}
      <div css={styles.partnerInfo}>
        {disableCTA ? (
          styledPartnerName
        ) : (
          <Link
            component={TrackedLink2}
            href={getPartnerPageUrl(partner.shortName)}
            trackingName="partner"
            css={styles.partnerName}
            onClick={onClickToTrack('description_page_partner')}
          >
            {styledPartnerName}
          </Link>
        )}
        {!disableCTA && (
          <Button
            variant="ghost"
            edgeAlign="start"
            size="small"
            css={[styles.learnMore, enableXDPRedesign && styles.learnMoreOverrides]}
            onClick={() => {
              setModalOpen();
              onClickToTrack('description_page_partner_learn_more')();
            }}
            component={TrackedButton}
            requireFullyVisible={false}
            withVisibilityTracking={false}
            trackingName={trackingName ?? 'partner_more'}
            aria-label={_t('Learn more about #{partnerName}', { partnerName: partner.name })}
          >
            {_t('Learn more')}
          </Button>
        )}
      </div>
      <PartnerModal open={isModalOpen} setOpen={setModalOpen} partner={partner} />
    </div>
  );
};

export default Partner;
